import { useEffect, type Dispatch, type SetStateAction } from 'react';
import {
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import { BottomDetailValueWithTooltip } from './BottomDetailValueWithTooltip';
import {
  DetailGroup,
  getBottomDetailHeader,
  getRowDetailContextLabel,
  isSentenceHtmlDetailKey,
  normalizeDetailValue,
  RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX,
  SHOW_HISTORIAL_SCORE,
  HISTORICAL_SCORE_COMPACT_MAX_HEIGHT,
  type BottomDetailTab,
} from '../utils/searchResultsHelpers';
import TabSelector from '@/global/TabSelector/TabSelector';
import { HistoricalScoreTabContent } from './HistoricalScoreTabContent';
import { getDetailValueLink, openEntityInfoPanel } from '../utils/detailValueLinks';
import { GtpTabContent } from './GtpTabContent';
import { CanopyTabContent } from './CanopyTabContent';
import { getGtpItemsFromRow } from '@/utils/formatGtpList';
import { isCanopyEnabled } from '@/config/canopy';
import { isEvidenceProfileEnabled } from '@/config/evidenceProfile';
import { EvidenceProfileTabContent } from './EvidenceProfileTabContent';
import AcademicPlanFeatureGate from '@/components/Modal/AcademicPlanFeatureGate';
import { isAcademicPlanFeatureRestricted } from '@/utils/premiumPlanFeatures';

interface RelationSummary {
  leftEntity: string;
  rightEntity: string;
  showRelationArrow: boolean;
  diseaseType: string;
  score: string;
  pmids: string;
  chem: string;
  variant: string;
}

interface BottomDetailPanelProps {
  bottomDetailRow: any;
  bottomDetailHeight: number;
  groupedBottomDetails: DetailGroup[];
  relationSummary: RelationSummary;
  source: any;
  tab: any;
  isSidebarOpen: boolean;
  sidebarWidth: number | string;
  isDesktop: boolean;
  hasLeftPanel?: boolean;
  searchParams: any;
  setSearchParams: (params: any) => void;
  onClose: () => void;
  startBottomResize: (startY: number) => void;
  bottomDetailTab: BottomDetailTab;
  setBottomDetailTab: (tab: BottomDetailTab) => void;
  setBottomDetailHeight?: Dispatch<SetStateAction<number>>;
  ensureTablesViewForDrilldown?: () => void;
  onRevealEvidenceTable?: () => void;
  isHistoricalEvidencePeek?: boolean;
  isAcademic?: boolean;
  isAuthenticated?: boolean;
  gtpOnly?: boolean;
  bottomDetailCellValues?: Record<string, unknown> | null;
}

export function BottomDetailPanel({
  bottomDetailRow,
  bottomDetailHeight,
  groupedBottomDetails,
  relationSummary,
  source,
  tab,
  isSidebarOpen,
  sidebarWidth,
  isDesktop,
  hasLeftPanel = false,
  searchParams,
  setSearchParams,
  onClose,
  startBottomResize,
  bottomDetailTab,
  setBottomDetailTab,
  setBottomDetailHeight,
  ensureTablesViewForDrilldown,
  onRevealEvidenceTable,
  isHistoricalEvidencePeek = false,
  isAcademic = false,
  isAuthenticated = false,
  gtpOnly = false,
  bottomDetailCellValues = null,
}: BottomDetailPanelProps) {
  const renderEntryValue = (entry: { key: string; value: unknown }) => {
    const linkedValue = getDetailValueLink(entry.key, entry.value);
    if (linkedValue?.type === 'external') {
      const textValue = normalizeDetailValue(entry.value, entry.key);
      return (
        <a
          href={linkedValue.href}
          target="_blank"
          rel="noopener noreferrer"
          className="w-60 min-w-0 max-w-60 shrink-0 truncate text-right text-xs font-medium text-pink underline underline-offset-2"
          title={textValue}
        >
          {textValue}
        </a>
      );
    }
    if (linkedValue?.type === 'entity') {
      const textValue = normalizeDetailValue(entry.value, entry.key);
      return (
        <button
          type="button"
          className="w-60 min-w-0 max-w-60 shrink-0 truncate bg-transparent p-0 text-right text-xs font-medium text-pink underline underline-offset-2"
          title={textValue}
          onClick={() => openEntityInfoPanel(linkedValue.entityView, linkedValue.item)}
        >
          {textValue}
        </button>
      );
    }
    const isSentenceHtml = isSentenceHtmlDetailKey(entry.key);
    const sentenceHtml = isSentenceHtml && typeof entry.value === 'string' ? entry.value.trim() : '';
    return sentenceHtml ? (
      <BottomDetailValueWithTooltip html={sentenceHtml} allowCopy />
    ) : (
      <BottomDetailValueWithTooltip text={normalizeDetailValue(entry.value, entry.key)} />
    );
  };

  useEffect(() => {
    if (!bottomDetailRow || !setBottomDetailHeight || isHistoricalEvidencePeek) {
      return;
    }
    const needsTallPanel =
      bottomDetailTab === 'historical' ||
      bottomDetailTab === 'canopy' ||
      bottomDetailTab === 'gtp' ||
      bottomDetailTab === 'evidence';
    if (!needsTallPanel) return;
    if (bottomDetailTab === 'historical' && isDesktop) return;

    const target =
      bottomDetailTab === 'canopy' || bottomDetailTab === 'evidence'
        ? Math.min(700, Math.max(520, Math.round(window.innerHeight * 0.55)))
        : Math.min(700, Math.max(300, Math.round(window.innerHeight * 0.5)));
    setBottomDetailHeight(prev => (prev < target ? target : prev));
  }, [bottomDetailRow, bottomDetailTab, isDesktop, isHistoricalEvidencePeek, setBottomDetailHeight]);

  if (!bottomDetailRow) return null;

  const normalizedTab = String(tab ?? '').toUpperCase();
  const gtpFromRow = getGtpItemsFromRow(bottomDetailRow);
  const gtpFromCells = gtpFromRow.length === 0 ? getGtpItemsFromRow(bottomDetailCellValues) : [];
  const gtpItems = gtpFromRow.length > 0 ? gtpFromRow : gtpFromCells;
  const hasGtpData = gtpItems.length > 0;

  const showGtpTab = hasGtpData || normalizedTab === 'GDA' || normalizedTab === 'GENES';
  const rightOffset = isDesktop ? (isSidebarOpen ? sidebarWidth : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX) : 0;
  const leftOffset = isDesktop && hasLeftPanel ? 340 : 0;

  const showHistoricalScoreTab =
    !gtpOnly && ((SHOW_HISTORIAL_SCORE && normalizedTab === 'GDA') || normalizedTab === 'VDA');
  const showEvidenceProfileTab =
    isEvidenceProfileEnabled && !gtpOnly && (normalizedTab === 'GDA' || normalizedTab === 'VDA');
  const showGtpTabOption = !gtpOnly && showGtpTab;
  const showCanopyTab =
    isCanopyEnabled &&
    !gtpOnly &&
    (normalizedTab === 'GDA' ||
      normalizedTab === 'VDA' ||
      normalizedTab === 'DDA' ||
      normalizedTab === 'DISEASES');
  const showDetailTabs =
    showHistoricalScoreTab || showEvidenceProfileTab || showGtpTabOption || showCanopyTab;
  const isPremiumDetailFeatureRestricted = isAcademicPlanFeatureRestricted(isAcademic, isAuthenticated);
  let activeBottomDetailTab: BottomDetailTab = bottomDetailTab;
  if (gtpOnly) {
    activeBottomDetailTab = 'gtp';
  } else if (bottomDetailTab === 'evidence' && !showEvidenceProfileTab) {
    activeBottomDetailTab = 'summary';
  } else if (bottomDetailTab === 'gtp' && !showGtpTab) {
    activeBottomDetailTab = 'summary';
  } else if (bottomDetailTab === 'canopy' && !showCanopyTab) {
    activeBottomDetailTab = 'summary';
  }

  const tabOptions = [
    { label: 'Summary', value: 'summary' },
    ...(showEvidenceProfileTab ? [{ label: 'Evidence profile', value: 'evidence' }] : []),
    ...(showHistoricalScoreTab ? [{ label: 'Historical score', value: 'historical' }] : []),
    ...(showGtpTabOption ? [{ label: 'GTAP', value: 'gtp' }] : []),
    ...(showCanopyTab ? [{ label: 'Canopy', value: 'canopy' }] : []),
  ];

  const detailHeader = getBottomDetailHeader(
    activeBottomDetailTab,
    relationSummary,
    getRowDetailContextLabel(tab as string),
    {
      ...((bottomDetailRow ?? {}) as Record<string, unknown>),
      ...((bottomDetailCellValues ?? {}) as Record<string, unknown>),
    },
  );

  return (
    <div
      data-testid="bottom-detail-panel"
      className="fixed bottom-0 left-0 right-0 z-30 flex flex-col overflow-hidden border-t border-slate-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
      style={{
        height: bottomDetailHeight,
        right: rightOffset,
        left: leftOffset,
        transition: 'right 0.3s ease-out, left 0.3s ease-out',
      }}
    >
      <div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-pink-50 to-white px-2 py-1.5 sm:px-4 sm:py-2">
        <div
          className="mx-auto flex w-fit cursor-grab items-center justify-center gap-2 px-3 py-1 text-center text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 active:bg-slate-200"
          onMouseDown={event => {
            event.preventDefault();
            startBottomResize(event.clientY);
          }}
          onTouchStart={event => {
            if (!event.touches.length) return;
            startBottomResize(event.touches[0].clientY);
          }}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Drag to resize detail panel"
          title="Drag to resize"
        >
          <span className="drag-handle" style={{ rotate: '90deg' }}>
            ⋮⋮
          </span>
        </div>
        <div
          className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${
            isHistoricalEvidencePeek ? 'mt-1' : 'mt-3 gap-3'
          }`}
        >
          <div className="flex min-w-0 items-start gap-2 sm:items-center">
            {!isHistoricalEvidencePeek && (
              <span className="shrink-0 rounded-xl bg-pink p-2 sm:mr-2">
                <SparklesIcon className="h-5 w-5 text-white" />
              </span>
            )}
            <div className="flex min-w-0 flex-col gap-1">
              {!isHistoricalEvidencePeek && (
                <span className="text-xs font-normal uppercase tracking-widest text-gray-400">
                  {detailHeader.contextLabel}
                </span>
              )}
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span
                  className={`font-semibold ${
                    activeBottomDetailTab === 'canopy' ? '' : 'uppercase'
                  } ${isHistoricalEvidencePeek ? 'text-sm' : 'text-base sm:text-lg'}`}
                >
                  {detailHeader.primaryEntity}
                </span>
                {detailHeader.showRelationArrow && (
                  <>
                    <ArrowsRightLeftIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className={isHistoricalEvidencePeek ? 'text-xs' : 'text-xs sm:text-sm'}>
                      {detailHeader.secondaryEntity}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div
            className={`flex shrink-0 items-center justify-end gap-2 ${
              isHistoricalEvidencePeek ? '' : 'border-t border-slate-100 pt-3 sm:border-t-0 sm:pt-0'
            }`}
          >
            {!isHistoricalEvidencePeek && (
              <SharedBadge
                text={`Source: ${source}`}
                icon={<SparklesIcon className="mr-1 h-4 w-4" />}
                variant="primaryLight"
                className="mb-0"
              />
            )}
            <SharedButton
              type="button"
              variant="primaryOutline"
              size="xs"
              iconRight={<ArrowTopRightOnSquareIcon className="mb-1 h-4 w-4" />}
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams as any);
                nextParams.set('fullRecord', '1');
                setSearchParams(nextParams);
              }}
            >
              <span className="hidden sm:inline">Open full record</span>
              <span className="sm:hidden">Full record</span>
            </SharedButton>
            <XMarkIcon
              className="mb-1 h-6 w-6 shrink-0 cursor-pointer sm:h-4 sm:w-4"
              strokeWidth={2}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
      {showDetailTabs && (
        <div
          className="shrink-0 border-b border-slate-100 bg-white px-2 py-1.5 sm:px-3 sm:py-2"
          role="tablist"
          aria-label="Row detail sections"
          data-demo-tour="historical-score-tab"
        >
          <TabSelector
            options={tabOptions}
            variant="primary"
            size="2xs"
            value={activeBottomDetailTab}
            onChange={v => setBottomDetailTab(v as BottomDetailTab)}
          />
        </div>
      )}
      <div
        className={`flex min-h-0 flex-1 flex-col ${
          isHistoricalEvidencePeek ? 'overflow-hidden p-1.5 sm:p-2' : 'overflow-y-auto p-2 sm:p-3'
        }`}
      >
        {activeBottomDetailTab === 'gtp' ? (
          <div className="min-w-0">
            <AcademicPlanFeatureGate featureName="GTAP" isRestricted={isPremiumDetailFeatureRestricted}>
              <GtpTabContent
                row={bottomDetailRow}
                cellValues={bottomDetailCellValues}
                title="Gene Therapeutic Area Profile"
              />
            </AcademicPlanFeatureGate>
          </div>
        ) : activeBottomDetailTab === 'canopy' && showCanopyTab ? (
          <div className="min-w-0">
            <AcademicPlanFeatureGate featureName="Canopy" isRestricted={isPremiumDetailFeatureRestricted}>
              <CanopyTabContent row={bottomDetailRow} />
            </AcademicPlanFeatureGate>
          </div>
        ) : showHistoricalScoreTab && activeBottomDetailTab === 'historical' ? (
          <div className="min-w-0">
            <AcademicPlanFeatureGate
              featureName="Historical score"
              isRestricted={isPremiumDetailFeatureRestricted}
            >
              <HistoricalScoreTabContent
                row={bottomDetailRow}
                source={String(source)}
                tab={String(tab)}
                onRevealEvidenceTable={onRevealEvidenceTable}
                ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
                compact={
                  isHistoricalEvidencePeek && bottomDetailHeight <= HISTORICAL_SCORE_COMPACT_MAX_HEIGHT
                }
              />
            </AcademicPlanFeatureGate>
          </div>
        ) : showEvidenceProfileTab && activeBottomDetailTab === 'evidence' ? (
          <div className="min-w-0">
            <AcademicPlanFeatureGate
              featureName="Evidence profile"
              isRestricted={isPremiumDetailFeatureRestricted}
            >
              <EvidenceProfileTabContent
                row={bottomDetailRow}
                source={String(source ?? '')}
                tab={String(tab ?? '')}
                onClose={onClose}
                ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />
            </AcademicPlanFeatureGate>
          </div>
        ) : (
          <div className="min-w-0 grid grid-cols-1 gap-3 xl:grid-cols-3">
            {groupedBottomDetails.map(group => (
              <section
                key={`bottom-${group.title}`}
                className="min-w-0 rounded-xl border border-slate-200 pt-0 pb-3"
              >
                <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 rounded-t-xl border-b border-slate-200 bg-gradient-to-r from-pink-50 to-white px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">
                  <span className="min-w-0 truncate">• {group.title}</span>
                  <span
                    className="shrink-0 tabular-nums font-light text-gray-500"
                    aria-label={`${group.entries.length} fields in this group`}
                    style={{ fontSize: '0.7rem' }}
                  >
                    {group.entries.length}
                  </span>
                </h3>
                <div className="space-y-1 px-3">
                  {group.entries.map(entry => {
                    const isSentenceHtml = isSentenceHtmlDetailKey(entry.key);
                    return (
                      <div
                        key={`${group.title}-${entry.key}`}
                        className={
                          isSentenceHtml
                            ? 'flex min-w-0 items-start gap-2 text-xs leading-[1.1]'
                            : 'flex min-w-0 items-center justify-between gap-2 text-xs leading-[1.1]'
                        }
                      >
                        <span
                          className="w-[44%] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-slate-500 tracking-wide"
                          title={entry.displayLabel}
                        >
                          {entry.displayLabel}
                        </span>
                        {renderEntryValue(entry)}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
